import React from "react";
import { Box, Button, Typography, useTheme, Stack } from "@mui/material";
import { NavigateBefore, NavigateNext, CheckCircle, CheckCircleOutline } from "@mui/icons-material";

export default function PreviousNextNavigation({
  prevItem,
  nextItem,
  completed,
  onNavigate,
  onToggleComplete
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: "center",
        mt: 6,
        mb: 4,
        pt: 4,
        pb: 2,
        px: { xs: 1, md: 2 },
        borderTop: `1px solid ${theme.palette.divider}`,
        gap: 3,
      }}
    >
      {/* ⬅️ Previous Button */}
      <Box sx={{ flex: 1, width: "100%", display: "flex", justifyContent: "flex-start" }}>
        {prevItem ? (
          <Box
            onClick={() => onNavigate(prevItem)}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              p: 2,
              borderRadius: 3,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              width: "100%",
              maxWidth: { xs: "100%", md: "260px" },
              background: isDark
                ? "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))"
                : "linear-gradient(135deg, rgba(0,0,0,0.02), rgba(0,0,0,0.005))",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                transform: "translateX(-6px)",
                background: isDark
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.03)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <NavigateBefore fontSize="small" sx={{ color: theme.palette.primary.main, mr: 0.5 }} />
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ letterSpacing: 1.5, color: theme.palette.text.secondary }}
              >
                PREVIOUS
              </Typography>
            </Box>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.3
              }}
            >
              {prevItem.title.replace(/^\d+(\.\d+)?\s*/, "")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
      </Box>

      {/* ✅ Complete Button */}
      <Box sx={{ order: { xs: -1, md: 0 }, width: { xs: "100%", md: "auto" }, textAlign: "center" }}>
        <Button
          variant={completed ? "contained" : "outlined"}
          color={completed ? "success" : "primary"}
          onClick={onToggleComplete}
          startIcon={completed ? <CheckCircle /> : <CheckCircleOutline />}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: "50px",
            fontWeight: 700,
            textTransform: "none",
            fontSize: "0.95rem",
            width: { xs: "100%", md: "auto" },
            boxShadow: completed && !isDark 
              ? "0 4px 14px rgba(46, 125, 50, 0.4)"
              : completed && isDark
              ? "0 4px 14px rgba(0, 0, 0, 0.4)"
              : "none",
            transition: "all 0.2s ease-in-out"
          }}
        >
          {completed ? "Lesson Completed" : "Mark as Complete"}
        </Button>
      </Box>

      {/* ➡️ Next Button */}
      <Box sx={{ flex: 1, width: "100%", display: "flex", justifyContent: "flex-end" }}>
        {nextItem ? (
          <Box
            onClick={() => onNavigate(nextItem)}
            sx={{
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              p: 2,
              borderRadius: 3,
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              width: "100%",
              maxWidth: { xs: "100%", md: "260px" },
              background: isDark
                ? "linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))"
                : "linear-gradient(135deg, rgba(0,0,0,0.02), rgba(0,0,0,0.005))",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
              textAlign: "right",
              "&:hover": {
                borderColor: theme.palette.primary.main,
                transform: "translateX(6px)",
                background: isDark
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.03)",
              }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
              <Typography
                variant="caption"
                fontWeight={800}
                sx={{ letterSpacing: 1.5, color: theme.palette.text.secondary }}
              >
                NEXT
              </Typography>
              <NavigateNext fontSize="small" sx={{ color: theme.palette.primary.main, ml: 0.5 }} />
            </Box>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.3
              }}
            >
              {nextItem.title.replace(/^\d+(\.\d+)?\s*/, "")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1 }} />
        )}
      </Box>
    </Box>
  );
}
